// Chart.js v4 registration for tree-shaking
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register all Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helper to get muted foreground color from CSS variables (theme-aware)
const getMutedForegroundColor = () => {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const style = getComputedStyle(document.documentElement);
    const color = style.getPropertyValue('--muted-foreground').trim();
    return color || '#94a3b8'; // fallback
  }
  return '#94a3b8';
};

// Set global defaults for Chart.js v4
ChartJS.defaults.font.family = "'IBM Plex Sans', sans-serif";
ChartJS.defaults.color = getMutedForegroundColor();

export { ChartJS };

// Helper to convert old v2/v3 options to v4 format
export const convertChartOptions = (oldOptions) => {
  // Get theme-aware muted foreground color
  const mutedColor = getMutedForegroundColor();
  
  if (!oldOptions) return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: mutedColor,
        bodyColor: '#1d252d',
        borderColor: '#dde5ed',
        borderWidth: 1,
        padding: 10,
      },
    },
  };
  
  // Check if already v4 format (has scales.x or scales.y directly, not arrays)
  const isV4Format = oldOptions.scales && (oldOptions.scales.x || oldOptions.scales.y) && !oldOptions.scales.xAxes && !oldOptions.scales.yAxes;
  if (isV4Format) {
    // Already v4 format, just ensure plugins exist
    return {
      ...oldOptions,
      plugins: oldOptions.plugins || {
        legend: { display: false },
        tooltip: { enabled: true },
      },
    };
  }
  
  const newOptions = {
    responsive: oldOptions.responsive !== false,
    maintainAspectRatio: oldOptions.maintainAspectRatio !== undefined ? oldOptions.maintainAspectRatio : false,
  };
  
  // Initialize plugins
  newOptions.plugins = oldOptions.plugins ? { ...oldOptions.plugins } : {};
  
  // Convert tooltips to plugins.tooltip
  if (oldOptions.tooltips) {
    // If tooltips are disabled via enabled: false, use that
    if (oldOptions.tooltips.enabled === false) {
      newOptions.plugins.tooltip = { enabled: false };
    } else {
      newOptions.plugins.tooltip = {
        enabled: true,
        backgroundColor: oldOptions.tooltips.backgroundColor || '#fff',
        titleColor: oldOptions.tooltips.titleFontColor || mutedColor,
        bodyColor: oldOptions.tooltips.bodyFontColor || '#1d252d',
        borderColor: oldOptions.tooltips.borderColor || '#dde5ed',
        borderWidth: oldOptions.tooltips.borderWidth || 1,
        padding: oldOptions.tooltips.xPadding || oldOptions.tooltips.padding || 10,
        titleFont: {
          family: oldOptions.tooltips.titleFontFamily || "'IBM Plex Sans', sans-serif",
          size: oldOptions.tooltips.titleFontSize || 12,
        },
        bodyFont: {
          family: oldOptions.tooltips.bodyFontFamily || "'IBM Plex Sans', sans-serif",
          size: oldOptions.tooltips.bodyFontSize || 14,
          weight: oldOptions.tooltips.bodyFontStyle || 'normal',
        },
        // Handle custom tooltip (external tooltip handler)
        ...(oldOptions.tooltips.custom && { external: oldOptions.tooltips.custom }),
      };
    }
  } else {
    newOptions.plugins.tooltip = {
      enabled: true,
      backgroundColor: '#fff',
      titleColor: mutedColor,
      bodyColor: '#1d252d',
      borderColor: '#dde5ed',
      borderWidth: 1,
      padding: 10,
    };
  }
  
  // Convert legend to plugins.legend
  if (oldOptions.legend) {
    newOptions.plugins.legend = {
      display: oldOptions.legend.display !== false,
      labels: {
        color: oldOptions.legend.labels?.fontColor || mutedColor,
      },
    };
  } else {
    newOptions.plugins.legend = { display: false };
  }
  
  // Convert hover options
  if (oldOptions.hover) {
    newOptions.interaction = {
      mode: oldOptions.hover.mode || 'nearest',
      intersect: oldOptions.hover.intersect !== false,
    };
    // onHover callback moves to options root
    if (oldOptions.hover.onHover) {
      newOptions.onHover = oldOptions.hover.onHover;
    }
  }
  
  // Convert scales from yAxes/xAxes arrays to y/x objects
  if (oldOptions.scales) {
    newOptions.scales = {};
    
    if (oldOptions.scales.yAxes && oldOptions.scales.yAxes[0]) {
      const yAxis = oldOptions.scales.yAxes[0];
      newOptions.scales.y = {
        beginAtZero: yAxis.ticks?.beginAtZero !== false,
        ticks: {
          display: yAxis.ticks?.display !== false,
          color: yAxis.ticks?.fontColor || mutedColor,
          font: {
            size: yAxis.ticks?.fontSize || 14,
            family: yAxis.ticks?.fontFamily || "'IBM Plex Sans', sans-serif",
          },
        },
        grid: {
          display: yAxis.gridLines?.display !== false,
          drawBorder: yAxis.gridLines?.drawBorder !== false,
          lineWidth: yAxis.gridLines?.lineWidth || 1,
        },
      };
      
      // Add title if scaleLabel exists
      if (yAxis.scaleLabel && yAxis.scaleLabel.display) {
        newOptions.scales.y.title = {
          display: true,
          text: yAxis.scaleLabel.labelString || '',
          color: yAxis.scaleLabel.fontColor || mutedColor,
          font: {
            family: yAxis.scaleLabel.fontFamily || "'IBM Plex Sans', sans-serif",
            size: yAxis.scaleLabel.fontSize || 14,
          },
        };
      }
    } else {
      // Default y scale
      newOptions.scales.y = {
        beginAtZero: true,
        grid: { display: false },
        ticks: { color: mutedColor },
      };
    }
    
    if (oldOptions.scales.xAxes && oldOptions.scales.xAxes[0]) {
      const xAxis = oldOptions.scales.xAxes[0];
      newOptions.scales.x = {
        offset: xAxis.offset !== false,
        ticks: {
          display: xAxis.ticks?.display !== false,
          color: xAxis.ticks?.fontColor || mutedColor,
          autoSkip: xAxis.ticks?.autoSkip,
          maxTicksLimit: xAxis.ticks?.maxTicksLimit,
          font: {
            size: xAxis.ticks?.fontSize || 14,
            family: xAxis.ticks?.fontFamily || "'IBM Plex Sans', sans-serif",
          },
        },
        grid: {
          display: xAxis.gridLines?.display !== false,
          drawBorder: xAxis.gridLines?.drawBorder !== false,
          lineWidth: xAxis.gridLines?.lineWidth || 0,
        },
      };
      
      // Handle time scale
      if (xAxis.type === 'time') {
        newOptions.scales.x.type = 'time';
        newOptions.scales.x.time = xAxis.time;
      }
      
      // Handle bar thickness (these go on dataset or scale)
      if (xAxis.barThickness) newOptions.scales.x.barThickness = xAxis.barThickness;
      if (xAxis.maxBarThickness) newOptions.scales.x.maxBarThickness = xAxis.maxBarThickness;
      if (xAxis.minBarLength) newOptions.scales.x.minBarLength = xAxis.minBarLength;
      
      // Add title if scaleLabel exists
      if (xAxis.scaleLabel && xAxis.scaleLabel.display) {
        newOptions.scales.x.title = {
          display: true,
          text: xAxis.scaleLabel.labelString || '',
          color: xAxis.scaleLabel.fontColor || mutedColor,
          font: {
            family: xAxis.scaleLabel.fontFamily || "'IBM Plex Sans', sans-serif",
            size: xAxis.scaleLabel.fontSize || 14,
          },
        };
      }
    } else {
      // Default x scale
      newOptions.scales.x = {
        offset: true,
        grid: { display: false },
        ticks: { color: mutedColor },
      };
    }
  } else {
    // Default scales
    newOptions.scales = {
      x: { offset: true, grid: { display: false }, ticks: { color: mutedColor } },
      y: { beginAtZero: true, grid: { display: false }, ticks: { color: mutedColor } },
    };
  }
  
  // Preserve onClick handler
  if (oldOptions.onClick) {
    newOptions.onClick = oldOptions.onClick;
  }
  
  return newOptions;
};
